// frontend/user/src/components/create-menu.tsx
// NC2CG 弹框-新建文件 共享组件:FAB + dialog(新建文件夹 + 上传文件)。
// 由 file-browser(parentId=null,根目录)与 folder-detail(parentId=:id,当前文件夹)复用。
// 对齐 ui/dogdisk-user.pen Node ID: NC2CG。
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@dogdisk/shared'
import { useFileUpload } from '../_lib/use-file-upload'
import { useDraggableFab } from '../_lib/use-draggable-fab'
import { UploadProgressPopup } from './upload-progress-popup'

export function CreateMenu({
  parentId,
  onChanged,
}: {
  parentId: string | null
  onChanged?: () => void
}) {
  const qc = useQueryClient()
  const fab = useDraggableFab()
  const [showCreateMenu, setShowCreateMenu] = useState(false)
  const [folderName, setFolderName] = useState('')
  const [folderInput, setFolderInput] = useState(false)

  const upload = useFileUpload({
    ...(parentId ? { parentId } : {}),
    onUploaded: () => {
      setShowCreateMenu(false)
      onChanged?.()
    },
  })

  // B3-#5 复用 POST /gapi/user/folders;错误(name 空 / 重名 / 超长)由 mutation.error 驱动 UI 提示。
  const createFolder = useMutation({
    mutationFn: (name: string) =>
      apiClient.post<{ id: string }>('/gapi/user/folders', { parent_id: parentId, name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['file', 'list', parentId] })
      setFolderName('')
      setFolderInput(false)
      setShowCreateMenu(false)
      onChanged?.()
    },
  })

  const folderNameTrimmed = folderName.trim()
  const folderTooLong = folderNameTrimmed.length > 64
  const folderInvalid = folderNameTrimmed.length === 0 || folderTooLong

  return (
    <>
      <button
        type="button"
        data-testid="files-fab-create-btn"
        onClick={fab.guardClick(() => setShowCreateMenu(true))}
        {...fab.dragProps}
        aria-label="新建"
        className="fixed z-20 flex h-14 w-14 cursor-grab touch-none items-center justify-center rounded-full bg-[#1971ca] text-white shadow-[0_8px_24px_rgba(25,113,202,0.45)] active:bg-[#155ca8] active:cursor-grabbing"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>

      {/* NC2CG 弹框-新建文件:对齐 ui/dogdisk-user.pen Node ID: NC2CG */}
      {showCreateMenu && (
        <div
          data-frame-id="NC2CG"
          data-testid="NC2CG"
          role="dialog"
          aria-modal="true"
          aria-label="新建"
          className="fixed inset-0 z-40 flex items-end bg-black/50"
          onClick={() => {
            setShowCreateMenu(false)
            setFolderInput(false)
          }}
        >
          <div
            className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
            style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* HandleBar */}
            <div className="flex h-6 items-center justify-center">
              <span className="h-1 w-10 rounded-full bg-[#E5E7EB]" />
            </div>
            {/* TitleRow:NC2CG fontSize 22 / 700 */}
            <div className="flex justify-center px-6 py-4">
              <span className="text-[22px] font-bold text-[#1A1A1A]" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>
                新建
              </span>
            </div>
            {folderInput ? (
              <div className="flex flex-col gap-3 px-6 py-4">
                <input
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !folderInvalid && !createFolder.isPending) {
                      createFolder.mutate(folderNameTrimmed)
                    }
                  }}
                  placeholder="文件夹名称(1-64 字符)"
                  maxLength={80}
                  aria-invalid={folderTooLong}
                  className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC] aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-[#FF6B6B]"
                  autoFocus
                />
                <div className="flex items-center justify-between text-[11px]">
                  {folderTooLong ? (
                    <span className="text-[#FF6B6B]">超过 64 字符上限</span>
                  ) : createFolder.error ? (
                    <span className="text-[#FF6B6B]">
                      {createFolder.error instanceof Error ? createFolder.error.message : '创建失败,请重试'}
                    </span>
                  ) : (
                    <span className="text-[#9CA3AF]">在当前目录下新建</span>
                  )}
                  <span className="text-[#9CA3AF]">{folderNameTrimmed.length}/64</span>
                </div>
                <button
                  type="button"
                  data-testid="files-folder-create-btn"
                  disabled={folderInvalid || createFolder.isPending}
                  onClick={() => createFolder.mutate(folderNameTrimmed)}
                  className="h-12 rounded-lg bg-[#19caad] text-[14px] font-semibold text-white disabled:opacity-50"
                >
                  {createFolder.isPending ? '创建中…' : '创建'}
                </button>
              </div>
            ) : (
              /* ActionList:NC2CG 顺序 = action1 新建文件夹(蓝)→ action2 上传文件(青);各 56px,gap 16,padding [0,24] */
              <ul className="flex flex-col">
                <li>
                  <button
                    type="button"
                    data-testid="NC2CG-action1"
                    data-action="create-folder"
                    onClick={() => setFolderInput(true)}
                    className="flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#F6F7F8]"
                  >
                    {/* folder-plus #60A5FA(NC2CG action1Icon) */}
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M4 4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8.5a2 2 0 0 1-1.7-.9l-.81-1.22A2 2 0 0 0 7.93 3H4Z" />
                      <path d="M12 11v6" />
                      <path d="M9 14h6" />
                    </svg>
                    <span className="text-[19px] text-[#1A1A1A]" style={{ fontFamily: 'DM Sans, ui-sans-serif, system-ui' }}>
                      新建文件夹
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    data-testid="NC2CG-action2"
                    data-action="upload-file"
                    onClick={() => upload.trigger()}
                    className="flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#F6F7F8]"
                  >
                    {/* upload #19caad(NC2CG action2Icon) */}
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#19caad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" x2="12" y1="3" y2="15" />
                    </svg>
                    <span className="text-[19px] text-[#1A1A1A]" style={{ fontFamily: 'DM Sans, ui-sans-serif, system-ui' }}>
                      上传文件
                    </span>
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      )}

      {/* 隐藏 file input + 进度 popup(由 useFileUpload hook 提供) */}
      {upload.inputElement}
      {upload.popupOpen && <UploadProgressPopup onClose={upload.closePopup} />}
    </>
  )
}
